import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import forEach from 'lodash/forEach';
import pickBy from 'lodash/pickBy';
import find from 'lodash/find';
import omit from 'lodash/omit';
import filter from 'lodash/filter';

import {
    ImeasurementPointList,
    ImeasurementPoint,
    ImeasurementPointListTab,
    IjobDefault,
    IinstallBase
} from '../models';

import { initialMeasurementPointList, initialMeasurementPoint } from './initialState';
import * as types from '../actions/actionTypes';
import { MANAGE_MEASUREMENT_POINT_LISTS_ASYNC_COMPLETE } from '../actions/actionTypes';
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';
import { IinitialState } from '.';
import { selectMainCategoryFromProductID } from './manageInventoryReducer';
import { MPLTypebyJobTypesID, measurementPointListTypeEnum } from '../models-enums';
import { constants, mainCategories } from '../constants/constants';
import { toastr } from 'react-redux-toastr';
import { getSelectedJobID, historyRowListType } from './commonSelectors';

// Track recent error messages to prevent duplicates
let lastErrorMessage = '';
let lastErrorTime = 0;
const ERROR_DEBOUNCE_MS = 1000; // Prevent same error within 1 second

const showErrorIfNotDuplicate = (title: string, message: string) => {
    const now = Date.now();
    if (message !== lastErrorMessage || now - lastErrorTime > ERROR_DEBOUNCE_MS) {
        lastErrorMessage = message;
        lastErrorTime = now;
        toastr.error(title, message, constants.toastrError);
    }
};

/*
 * HELPER FUNCTIONS
 */
export const cleanMeasurementPoints = (points: any): ImeasurementPoint[] => {
    return map(points, (point: any) => {
        return {
            ...initialMeasurementPoint,
            ...pickBy(point, property => property !== null)
        };
    });
};

export const cleanMeasurementPointList = (list: any): ImeasurementPointList => {
    const cleanedList = {
        ...initialMeasurementPointList,
        ...pickBy(list, property => property !== null)
    };

    const tabsWithoutMeasurementPoints = map(cleanedList.measurementPointTabs, tab => {
        return omit(tab, 'measurementPoints');
    });
    return {
        ...cleanedList,
        measurementPointTabs: tabsWithoutMeasurementPoints
    };
};

/*
 * SELECTORS
 */
const selectStandardByID = (state: IinitialState, props: { standardID: string }) =>
    state.productInfo.standards[props.standardID];
const selectMeasurementPointLists = (state: IinitialState) => state.measurementPointLists.measurementPointListsByID;

const getSelectedJob = (state: IinitialState) => state.manageJob.selectedJob;
const getShouldShowWarning = (state: IinitialState, props: { shouldShowWarning: boolean }) => props.shouldShowWarning;
const selectMeasurementPointsByID = (state: IinitialState) => state.measurementPointLists.measurementPointsByID;
const selectTabIDProp = (state: IinitialState, props: { tabID: string }) => props.tabID;
const getMeasurementPointProp = (state: IinitialState, props: { measurementPoint: ImeasurementPoint }) =>
    props.measurementPoint;

const getJobDefaults = (state: IinitialState) => state.measurementPointLists.jobDefaultsByID;
const getMeasurementPointResultsByID = (state: IinitialState) =>
    state.measurementPointResults.measurementPointResultsByID;
const getInstallBases = (state: IinitialState) => state.manageInventory.installBasesByID;
const getProducts = (state: IinitialState) => state.manageInventory.productsByID;
const location = (state: IinitialState) => state.location.pathname;

export const selectMeasurementPointResultsIDs = createSelector(
    [getMeasurementPointResultsByID],
    measurementPointResultsByID => Object.keys(measurementPointResultsByID)
);

export const selectJobDefaultsForJob = createSelector(
    [getSelectedJobID, getJobDefaults],
    (selectedJobID, jobDefaultsByID) => {
        // Potential optimization. jobDefaultsByID could be have a composite key of jobID and ID.
        return filter(jobDefaultsByID, jobDefault => {
            return jobDefault.jobID === selectedJobID;
        });
    }
);

export const selectMeasurementPointsForTabID = createSelector(
    [selectMeasurementPointsByID, selectTabIDProp],
    (measurementPointsByID, tabID) => {
        return keyBy(filter(measurementPointsByID, { measurementPointTabID: tabID }), 'id');
    }
);

export const selectJobDefaultForMeasurementPoint = createSelector(
    [
        getSelectedJobID,
        getJobDefaults,
        getMeasurementPointProp,
        selectMeasurementPointLists,
        getMeasurementPointResultsByID,
        getInstallBases,
        getProducts,
        location
    ],
    (
        jobID,
        jobDefaults,
        measurementPoint,
        measurementPointListsByID,
        measurementPointResultsByID,
        installBases,
        products,
        pathName
    ) => {
        let productTypeID: string | null = null;
        let listId: string | null = null;
        let installBaseId: string | null = null;
        let installBase: IinstallBase | null = null;

        // Look for measurementPointListID by tabID
        Object.values(measurementPointListsByID).forEach(list => {
            const mpList: any = list.measurementPointTabs.find(x => x.id === measurementPoint.measurementPointTabID);

            if (mpList) {
                listId = mpList.measurementPointListID;

                return;
            }
        });

        if (listId !== null) {
            // If a measurementPointList was found, now let's find a measurementPointResult with a maching measurementPointListID
            Object.values(measurementPointResultsByID).forEach(result => {
                if (result.measurementPointListID === listId) {
                    installBaseId = result.installBaseID;
                    return;
                }
            });

            // INFO: This is selector is called BEFORE measurementPointResults is updated and is therefore not pulling the current installBaseId when viewing a device.
            // Checking if the install base ID from the URL matches, if not, default to what's in the URL as that always seems to be correct.
            const devicesIndex = pathName.indexOf('/devices/');
            if (devicesIndex !== -1) {
                const deviceId = pathName.substring(devicesIndex + '/devices/'.length);
                if (installBaseId !== deviceId) {
                    installBaseId = deviceId;
                }
            }

            const installFromState = installBaseId ? installBases[installBaseId] : null;

            // If an installBase was found, now we can look up the product, and the glorious productTypeID that started this search
            if (installFromState !== null && installFromState !== undefined) {
                installBase = installFromState;

                if (installBase && installBase.materialID) {
                    const p = products[installBase.materialID];

                    if (p) {
                        productTypeID = p.productTypeID;
                    }
                }
            }
        }
        // Without filtering by productTypeID, the default answer with just pick the 1st one from the jobDefaults, which is wrong
        if (productTypeID !== null) {
            return find(
                jobDefaults,
                item =>
                    item.jobID === jobID &&
                    item.mpType === measurementPoint.type &&
                    item.mpDefaultType === measurementPoint.jobDefaultType &&
                    item.productTypeID.toLowerCase() === productTypeID?.toLowerCase()
            );
        } else {
            return find(
                jobDefaults,
                item =>
                    item.jobID === jobID &&
                    item.mpType === measurementPoint.type &&
                    item.mpDefaultType === measurementPoint.jobDefaultType
            );
        }
    }
);

// TODO this can be improved by checking to make sure the type is a valid type and then we could return as measurementPointListTypeEnum
const getMeasurementPointListTypeProp = (state: IinitialState, props: { measurementPointListType: string | number }) =>
    props.measurementPointListType ? parseInt(props.measurementPointListType.toString()) : null;

interface Query {
    type?: number;
    standardID: string;
    isDeleted: boolean;
    forJob?: boolean;
}

export const selectSimpleMeasurementPointList = createSelector(
    [
        selectMeasurementPointLists,
        selectStandardByID,
        getSelectedJob,
        getShouldShowWarning,
        getMeasurementPointListTypeProp
    ],
    (measurementPointListsByID, standard, selectedJob, shouldShowWarning, measurementPointListType) => {
        const query: Query = {
            standardID: standard?.id || '',
            isDeleted: false
        };

        if (measurementPointListType !== null) {
            query.type = measurementPointListType;
        }

        let forJob = true;

        // These two types are from the existing flow (these will be removed at some point)
        if (
            measurementPointListType === measurementPointListTypeEnum.purity ||
            measurementPointListType === measurementPointListTypeEnum.verificationChecklist
        ) {
            forJob = false;
        }

        // Going the path of readability over "coolness", long day, brain hurts
        let foundList: ImeasurementPointList | undefined = undefined;
        // If this a job level MPL then filter the main category to the Job category
        if (forJob) {
            foundList = find(measurementPointListsByID, (item: ImeasurementPointList) => {
                return (
                    item.standardID === query.standardID &&
                    item.isDeleted === query.isDeleted &&
                    query.type === item.type &&
                    item.mainCategoryID === mainCategories.job
                );
            });
        } else {
            // This should only be for the purity and verification checklist (old way)
            foundList = find(measurementPointListsByID, (item: ImeasurementPointList) => {
                return (
                    item.standardID === query.standardID &&
                    item.isDeleted === query.isDeleted &&
                    query.type === item.type
                );
            });
        }
        if (shouldShowWarning && foundList === undefined) {
            const mplTypeName = Object.keys(measurementPointListTypeEnum).find(
                key => (measurementPointListTypeEnum as any)[key] === measurementPointListType
            );
            const capitalizedMplType = mplTypeName ? mplTypeName.charAt(0).toUpperCase() + mplTypeName.slice(1) : 'N/A';

            showErrorIfNotDuplicate(
                'Error',
                `There are no measurement points defined for type, facility standard: ${capitalizedMplType}, ${standard?.name ||
                    'N/A'}.`
            );
        }
        // TODO populate the measurement points on each tab here rather than in the pointlistForm.tsx

        return foundList || initialMeasurementPointList;
    }
);

export const selectMeasurementPointListForInstallBase = createSelector(
    [
        selectMainCategoryFromProductID,
        selectMeasurementPointLists,
        selectStandardByID,
        getSelectedJob,
        getShouldShowWarning
    ],
    (category, measurementPointListsByID, standard, selectedJob, shouldShowWarning) => {
        const MPLType = MPLTypebyJobTypesID[selectedJob.jobTypeID];
        // if you believe you shouldn't be seeing this, please check Meassurements on the web app
        const foundList = find(measurementPointListsByID, {
            type: MPLType,
            mainCategoryID: category?.id || '',
            standardID: standard?.id || '',
            isDeleted: false
        });

        if (shouldShowWarning && foundList === undefined) {
            const mplTypeName = Object.keys(measurementPointListTypeEnum).find(
                key => (measurementPointListTypeEnum as any)[key] === MPLType
            );
            const capitalizedMplType = mplTypeName ? mplTypeName.charAt(0).toUpperCase() + mplTypeName.slice(1) : 'N/A';

            showErrorIfNotDuplicate(
                'Error',
                `There are no measurement points defined for category, type, facility standard: ${category?.name ||
                    'N/A'}, ${capitalizedMplType}, ${standard?.name || 'N/A'}.`
            );
        }
        // TODO populate the measurement points on each tab here rather than in the pointlistForm.tsx

        return foundList || initialMeasurementPointList;
    }
);

/*
 * The measurement point list a historical result has to be rendered against when it does not match
 * the current job.
 *
 * History spans job types since MMG-4345, so an Inspection can open a Service Plan visit. That
 * result's answers are keyed to the Service Plan list's points, so the overlay renders the list the
 * result was recorded under - the same thing Web's Last Inspection modal does
 * (docs/measurement-points-standards.md section 5).
 *
 * Returns undefined for a same-type result, which keeps today's behaviour exactly: the current
 * job's list, with MMG-4304's translateResultToList carrying answers across a standard rollover.
 *
 * A cross-type row's recorded list is often not on the device: the list it names is the version that
 * was current when the visit happened, and editing a list in Web replaces it. So the row falls back
 * to this asset's current list of the row's own job type, which is the same set of points under the
 * same labels - translateResultToList (MMG-4304) carries the answers onto it. It must never fall
 * back to *this job's* list, which is how MMG-4345 failed QA the first time.
 *
 * `installBaseID` is the asset on screen. A historical result recorded on a different asset has
 * nothing to say about this one, so it is ignored rather than allowed to swap this asset's list -
 * the ID outlives the screen it was clicked on.
 */
export const selectCrossJobTypeHistoricalList = (
    state: IinitialState,
    installBaseID: string
): ImeasurementPointList | undefined => {
    const { historicalResultID, measurementPointResultsByID } = state.measurementPointResults;

    if (historicalResultID.length === 0) {
        return undefined;
    }

    const result = measurementPointResultsByID[historicalResultID];

    if (result === undefined || result.installBaseID !== installBaseID) {
        return undefined;
    }

    const { measurementPointListsByID } = state.measurementPointLists;
    const rowType = historyRowListType(result, measurementPointListsByID);

    if (rowType === MPLTypebyJobTypesID[state.manageJob.selectedJob.jobTypeID]) {
        return undefined;
    }

    const recordedList = measurementPointListsByID[result.measurementPointListID];

    if (recordedList) {
        return recordedList;
    }

    // The recorded list is not on the device - this asset's current list of that type holds the
    // same points. Returning the empty list instead would take the whole screen down: the form
    // redirects to /devices when its list has no ID.
    const installBase = state.manageInventory.installBasesByID[installBaseID];
    const facility = state.facilities.facilitiesByID[state.manageJob.selectedJob.facilityID];
    const category = installBase ? selectMainCategoryFromProductID(state, { materialID: installBase.materialID }) : undefined;

    const currentListOfRowType = find(measurementPointListsByID, {
        type: rowType,
        mainCategoryID: category?.id || '',
        standardID: facility?.standardID || '',
        isDeleted: false
    });

    if (currentListOfRowType) {
        return currentListOfRowType;
    }

    console.error(
        `Measurement point list ${result.measurementPointListID}, recorded on historical result ${result.id}, is not on this device and this asset has no ${measurementPointListTypeEnum[rowType]} list to show it against.`
    );

    return undefined;
};

/*
 * REDUCERS
 */

/*
 * data - keyed by the MPLID
 */
function measurementPointListsReducer(
    state: { [key: string]: ImeasurementPointList } = {},
    action: any
): { [key: string]: ImeasurementPointList } {
    switch (action.type) {
        // REMOVED: Old processing action types - using async middleware instead
        case types.MANAGE_MEASUREMENT_POINT_LISTS_SUCCESS: {
            // If this action has asyncProcessing flag, middleware is handling the processing
            if (action.payload?.asyncProcessing) {
                return state; // Keep current state, async completion will update it
            }
            if (!action.allLists || (action.allLists && !action.allLists.length)) {
                console.error('[measurementPointListsReducer]: missing measurement point lists');
                return state;
            }
            const measurementPointLists = action.allLists.map((measurementPointList: ImeasurementPointList) => {
                return cleanMeasurementPointList(measurementPointList);
            });
            const keyedLists = keyBy(measurementPointLists, 'id');

            // If this is an incremental update, merge with existing state
            if (action.isIncremental) {
                return { ...state, ...keyedLists };
            } else {
                // First chunk or non-incremental update, replace state
                return keyedLists;
            }
        }
        case MANAGE_MEASUREMENT_POINT_LISTS_ASYNC_COMPLETE: {
            // Handle async processing completion
            const { processedMeasurementPointLists } = action.payload;
            if (processedMeasurementPointLists) {
                // The middleware provides keyed raw data, we need to clean it
                // Using direct assignment to avoid any potential performance issues
                const cleanedLists: { [key: string]: ImeasurementPointList } = {};
                Object.entries(processedMeasurementPointLists).forEach(([key, list]: [string, any]) => {
                    cleanedLists[key] = cleanMeasurementPointList(list);
                });
                return cleanedLists;
            }
            return state;
        }

        case types.MANAGE_MEASUREMENT_POINT_LIST_UPDATE: {
            if (action.measurementPointList) {
                return {
                    ...state,
                    [action.measurementPointList.id]: {
                        ...cleanMeasurementPointList(action.measurementPointList)
                    }
                };
            } else {
                return state;
            }
        }

        case types.USER_LOGOUT_SUCCESS:
            return {};
        default:
            return state;
    }
}
const measurementPointsByID = (
    state: { [key: string]: ImeasurementPoint } = {},
    action: any
): { [key: string]: ImeasurementPoint } => {
    switch (action.type) {
        case types.MANAGE_MEASUREMENT_POINT_LISTS_SUCCESS: {
            // If this action has asyncProcessing flag, middleware is handling the processing
            if (action.payload?.asyncProcessing) {
                return state; // Keep current state, async completion will update it
            }

            const measurmentPointLists = action.allLists;
            let chunkMeasurementPoints: { [key: string]: ImeasurementPoint } = {};

            // Process this chunk of lists
            forEach(measurmentPointLists, (list: ImeasurementPointList) => {
                forEach(list.measurementPointTabs, (tab: ImeasurementPointListTab) => {
                    chunkMeasurementPoints = {
                        ...chunkMeasurementPoints,
                        ...keyBy(cleanMeasurementPoints(tab.measurementPoints), 'id')
                    };
                });
            });

            // If this is an incremental update, merge with existing state
            if (action.isIncremental) {
                return { ...state, ...chunkMeasurementPoints };
            } else {
                // First chunk or non-incremental update, replace state
                return chunkMeasurementPoints;
            }
        }

        case types.MANAGE_MEASUREMENT_POINT_LIST_UPDATE: {
            let mPoints: ImeasurementPoint[] = [];
            forEach(action.measurementPointList.measurementPointTabs, mTab => {
                mPoints = [...mPoints, ...cleanMeasurementPoints(mTab.measurementPoints)];
            });
            return { ...state, ...keyBy(mPoints, 'id') };
        }
        case MANAGE_MEASUREMENT_POINT_LISTS_ASYNC_COMPLETE: {
            // Handle async processing completion
            const { processedMeasurementPointLists } = action.payload;
            if (processedMeasurementPointLists) {
                const allMeasurementPoints: { [key: string]: ImeasurementPoint } = {};

                // Extract measurement points from all tabs (raw data still has tabs)
                // Using direct assignment instead of object spreading to avoid O(n²) complexity
                Object.values(processedMeasurementPointLists).forEach((list: any) => {
                    if (list.measurementPointTabs) {
                        forEach(list.measurementPointTabs, (tab: ImeasurementPointListTab) => {
                            const cleanedPoints = cleanMeasurementPoints(tab.measurementPoints);
                            cleanedPoints.forEach(point => {
                                allMeasurementPoints[point.id] = point;
                            });
                        });
                    }
                });

                return allMeasurementPoints;
            }
            return state;
        }
        case types.USER_LOGOUT_SUCCESS:
            return {};
        default:
            return state;
    }
};

/*
 * data - keyed by the MPLID
 */
function jobDefaultsReducer(state: { [key: string]: IjobDefault } = {}, action: any): { [key: string]: IjobDefault } {
    switch (action.type) {
        case types.SAVE_JOB_DEFAULTS:
            return { ...state, ...keyBy(action.payload, 'id') };
        default:
            return state;
    }
}

const manageMeasurementPointLists = combineReducers({
    measurementPointListsByID: measurementPointListsReducer,
    jobDefaultsByID: jobDefaultsReducer,
    measurementPointsByID
});

export default manageMeasurementPointLists;
